import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import api from "@/lib/api";
import { useAuth, apiErr } from "@/context/AuthContext";
import { PageTitle } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Workers() {
  const nav = useNavigate();
  const { isEditor } = useAuth();
  const [type, setType] = useState("ALL");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const EMPTY = { worker_id: "", worker_name: "", worker_type: "PENGEPUL", phone: "", address: "", no_rekening: "", nama_bank: "", atas_nama: "" };
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => {
    const p = new URLSearchParams();
    if (type !== "ALL") p.append("worker_type", type);
    if (search) p.append("search", search);
    api.get(`/workers?${p.toString()}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [type, search]);

  const create = async () => {
    try {
      if (editing) {
        const { worker_id, ...upd } = form;
        await api.put(`/workers/${editing}`, upd);
        toast.success("Worker diperbarui");
      } else {
        await api.post("/workers", form);
        toast.success("Worker ditambahkan");
      }
      setOpen(false); setEditing(null); setForm(EMPTY);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const openEdit = (w) => {
    setEditing(w.worker_id);
    setForm({ worker_id: w.worker_id, worker_name: w.worker_name, worker_type: w.worker_type, phone: w.phone || "", address: w.address || "", no_rekening: w.no_rekening || "", nama_bank: w.nama_bank || "", atas_nama: w.atas_nama || "" });
    setOpen(true);
  };

  return (
    <div>
      <PageTitle title="Tenaga Kerja" desc="Master pengepul & penjahit dalam beserta subledger."
        right={isEditor && <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="btn-add-worker"><Plus className="w-4 h-4 mr-1.5" />Tambah Worker</Button>} />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Tabs value={type} onValueChange={setType}>
          <TabsList>
            <TabsTrigger value="ALL" data-testid="worker-tab-all">Semua</TabsTrigger>
            <TabsTrigger value="PENGEPUL" data-testid="worker-tab-pengepul">Pengepul</TabsTrigger>
            <TabsTrigger value="PENJAHIT_DALAM" data-testid="worker-tab-penjahit">Penjahit Dalam</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative sm:w-72">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input className="pl-8" placeholder="Cari ID / nama…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="worker-search" />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-left text-sm" data-testid="table-worker-subledger">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
              {["Worker ID", "Nama", "Tipe", "Telepon", "No. Rekening", "Atas Nama", "Status", ""].map((h) => <th key={h} className="px-4 py-2.5">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.worker_id} className="border-b border-slate-100 hover:bg-slate-50/80 cursor-pointer" onClick={() => nav(`/workers/${w.worker_id}`)} data-testid={`worker-row-${w.worker_id}`}>
                <td className="px-4 py-3 font-bold text-slate-900">{w.worker_id}</td>
                <td className="px-4 py-3 text-slate-800">{w.worker_name}</td>
                <td className="px-4 py-3 text-slate-600">{w.worker_type === "PENGEPUL" ? "Pengepul" : "Penjahit Dalam"}</td>
                <td className="px-4 py-3 text-slate-500">{w.phone || "-"}</td>
                <td className="px-4 py-3 text-slate-500 mono text-xs">{w.no_rekening ? `${w.no_rekening}${w.nama_bank ? " · " + w.nama_bank : ""}` : <span className="text-amber-600">belum diisi</span>}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{w.atas_nama || "-"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${w.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"}`}>{w.status}</span>
                </td>
                <td className="px-4 py-3 text-blue-600 font-medium whitespace-nowrap">
                  {isEditor && <button onClick={(e) => { e.stopPropagation(); openEdit(w); }} className="mr-3 text-slate-500 hover:text-slate-900" data-testid={`worker-edit-${w.worker_id}`}>Edit</button>}Lihat →
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Belum ada worker.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editing ? `Edit Worker ${editing}` : "Tambah Worker"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Worker ID</Label><Input value={form.worker_id} onChange={(e) => setForm({ ...form, worker_id: e.target.value })} placeholder="P051" disabled={!!editing} data-testid="worker-form-id" /></div>
              <div>
                <Label>Tipe</Label>
                <Select value={form.worker_type} onValueChange={(v) => setForm({ ...form, worker_type: v })}>
                  <SelectTrigger data-testid="worker-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENGEPUL">Pengepul</SelectItem>
                    <SelectItem value="PENJAHIT_DALAM">Penjahit Dalam</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Nama</Label><Input value={form.worker_name} onChange={(e) => setForm({ ...form, worker_name: e.target.value })} data-testid="worker-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>No. Telepon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="worker-form-phone" /></div>
              <div><Label>Alamat</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="worker-form-address" /></div>
              <div><Label>No. Rekening</Label><Input value={form.no_rekening} onChange={(e) => setForm({ ...form, no_rekening: e.target.value })} data-testid="worker-form-rekening" /></div>
              <div><Label>Nama Bank (opsional)</Label><Input value={form.nama_bank} onChange={(e) => setForm({ ...form, nama_bank: e.target.value })} data-testid="worker-form-bank" /></div>
              <div><Label>Atas Nama (opsional)</Label><Input value={form.atas_nama} onChange={(e) => setForm({ ...form, atas_nama: e.target.value })} data-testid="worker-form-atas-nama" /></div>
            </div>
            <p className="text-xs text-slate-400">Semua kolom kontak/rekening boleh kosong. Rekening kosong akan tercetak "belum diisi" di sheet totalan.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={create} className="bg-slate-900 text-white" data-testid="worker-form-submit">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
